import { supabase } from '../supabase';

interface AnalyticsEvent {
  event_type: 'page_view' | 'signup' | 'login' | 'logout' | 'interaction';
  page_url: string;
  page_title: string;
  user_id?: string;
  session_id: string;
  referrer?: string;
  user_agent: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

interface UserSession {
  session_id: string;
  user_id?: string;
  started_at: string;
  last_activity: string;
  page_views: number;
  referrer?: string;
  user_agent: string;
  is_authenticated: boolean;
}

class AnalyticsService {
  private sessionId: string;
  private isEnabled: boolean = false;
  private queue: AnalyticsEvent[] = [];
  private flushInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeSession();
    this.startFlushInterval();
  }

  /**
   * Initialize analytics service with GDPR consent
   */
  public initialize(hasAnalyticsConsent: boolean) {
    this.isEnabled = hasAnalyticsConsent;
    
    if (this.isEnabled) {
      console.log('[Analytics] Service enabled with user consent');
      this.trackPageView();
    } else {
      console.log('[Analytics] Service disabled - no user consent');
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize user session
   */
  private async initializeSession() {
    if (!this.isEnabled) return;

    try {
      const sessionData: UserSession = {
        session_id: this.sessionId,
        started_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
        page_views: 0,
        referrer: document.referrer || undefined,
        user_agent: navigator.userAgent,
        is_authenticated: false
      };

      // Store session in localStorage for persistence
      localStorage.setItem('analytics_session', JSON.stringify(sessionData));

      // Save to database
      await supabase
        .from('analytics_sessions')
        .insert(sessionData);

    } catch (error) {
      console.error('[Analytics] Failed to initialize session:', error);
    }
  }

  /**
   * Track page view
   */
  public trackPageView(customData?: Record<string, any>) {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      event_type: 'page_view',
      page_url: window.location.href,
      page_title: document.title,
      session_id: this.sessionId,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      metadata: {
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: screen.width,
        screen_height: screen.height,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        ...customData
      }
    };

    this.queueEvent(event);
    this.updateSessionActivity();
  }

  /**
   * Track user signup
   */
  public trackSignup(userId: string, signupMethod: 'email' | 'phone', metadata?: Record<string, any>) {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      event_type: 'signup',
      page_url: window.location.href,
      page_title: document.title,
      user_id: userId,
      session_id: this.sessionId,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      metadata: {
        signup_method: signupMethod,
        ...metadata
      }
    };

    this.queueEvent(event);
    this.updateUserSession(userId);
  }

  /**
   * Track user login
   */
  public trackLogin(userId: string, loginMethod: 'email' | 'phone' | 'username', metadata?: Record<string, any>) {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      event_type: 'login',
      page_url: window.location.href,
      page_title: document.title,
      user_id: userId,
      session_id: this.sessionId,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      metadata: {
        login_method: loginMethod,
        ...metadata
      }
    };

    this.queueEvent(event);
    this.updateUserSession(userId);
  }

  /**
   * Track user logout
   */
  public trackLogout(userId: string) {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      event_type: 'logout',
      page_url: window.location.href,
      page_title: document.title,
      user_id: userId,
      session_id: this.sessionId,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };

    this.queueEvent(event);
    this.updateUserSession(); // Remove user ID from session
  }

  /**
   * Track custom interaction
   */
  public trackInteraction(interactionType: string, metadata?: Record<string, any>) {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      event_type: 'interaction',
      page_url: window.location.href,
      page_title: document.title,
      session_id: this.sessionId,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      metadata: {
        interaction_type: interactionType,
        ...metadata
      }
    };

    this.queueEvent(event);
  }

  /**
   * Queue event for batch processing
   */
  private queueEvent(event: AnalyticsEvent) {
    // Add user ID if available
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      event.user_id = currentUser.id;
    }

    this.queue.push(event);

    // Flush immediately for critical events
    if (['signup', 'login', 'logout'].includes(event.event_type)) {
      this.flushEvents();
    }
  }

  /**
   * Get current authenticated user
   */
  private getCurrentUser() {
    try {
      const authData = localStorage.getItem(`sb-${window.location.hostname.split('.')[0]}-auth-token`);
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user;
      }
    } catch (error) {
      // Ignore parsing errors
    }
    return null;
  }

  /**
   * Update session activity
   */
  private updateSessionActivity() {
    try {
      const sessionData = localStorage.getItem('analytics_session');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        session.last_activity = new Date().toISOString();
        session.page_views = (session.page_views || 0) + 1;
        localStorage.setItem('analytics_session', JSON.stringify(session));
      }
    } catch (error) {
      console.error('[Analytics] Failed to update session activity:', error);
    }
  }

  /**
   * Update user session with user ID
   */
  private async updateUserSession(userId?: string) {
    try {
      const sessionData = localStorage.getItem('analytics_session');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        session.user_id = userId;
        session.is_authenticated = !!userId;
        session.last_activity = new Date().toISOString();
        localStorage.setItem('analytics_session', JSON.stringify(session));

        // Update in database with timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        );

        const dbPromise = supabase
          .from('analytics_sessions')
          .update({
            user_id: userId,
            is_authenticated: !!userId,
            last_activity: session.last_activity
          })
          .eq('session_id', this.sessionId);

        try {
          await Promise.race([dbPromise, timeoutPromise]);
        } catch (dbError) {
          console.warn('[Analytics] Session update timeout, continuing with local data');
        }
      }
    } catch (error) {
      console.error('[Analytics] Failed to update user session:', error);
    }
  }

  /**
   * Flush events to database
   */
  private async flushEvents() {
    if (!this.isEnabled || this.queue.length === 0) return;

    try {
      const eventsToFlush = [...this.queue];
      this.queue = [];

      // Add timeout to prevent gateway timeout issues
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 8000)
      );

      const dbPromise = supabase
        .from('analytics_events')
        .insert(eventsToFlush);

      try {
        await Promise.race([dbPromise, timeoutPromise]);
        console.log(`[Analytics] Flushed ${eventsToFlush.length} events`);
      } catch (dbError) {
        console.warn(`[Analytics] Failed to flush ${eventsToFlush.length} events, will retry later:`, dbError);
        // Re-queue failed events for retry
        this.queue.unshift(...eventsToFlush);
      }
    } catch (error) {
      console.error('[Analytics] Failed to flush events:', error);
    }
  }

  /**
   * Start periodic flush interval
   */
  private startFlushInterval() {
    this.flushInterval = setInterval(() => {
      this.flushEvents();
    }, 30000); // Flush every 30 seconds
  }

  /**
   * Stop analytics service
   */
  public stop() {
    this.isEnabled = false;
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flushEvents(); // Final flush
  }

  /**
   * Get analytics dashboard data
   */
  public async getDashboardData(timeRange: '24h' | '7d' | '30d' = '7d') {
    if (!this.isEnabled) return null;

    try {
      const startDate = new Date();
      switch (timeRange) {
        case '24h':
          startDate.setHours(startDate.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
      }

      const { data: events } = await supabase
        .from('analytics_events')
        .select('*')
        .gte('timestamp', startDate.toISOString());

      const { data: sessions } = await supabase
        .from('analytics_sessions')
        .select('*')
        .gte('started_at', startDate.toISOString());

      return {
        totalPageViews: events?.filter(e => e.event_type === 'page_view').length || 0,
        totalSessions: sessions?.length || 0,
        totalSignups: events?.filter(e => e.event_type === 'signup').length || 0,
        totalLogins: events?.filter(e => e.event_type === 'login').length || 0,
        uniqueUsers: new Set(events?.map(e => e.user_id).filter(Boolean)).size,
        topPages: this.getTopPages(events || []),
        topReferrers: this.getTopReferrers(events || []),
        userTypes: {
          authenticated: sessions?.filter(s => s.is_authenticated).length || 0,
          anonymous: sessions?.filter(s => !s.is_authenticated).length || 0
        }
      };
    } catch (error) {
      console.error('[Analytics] Failed to get dashboard data:', error);
      return null;
    }
  }

  /**
   * Get top pages by views
   */
  private getTopPages(events: AnalyticsEvent[]) {
    const pageViews = events.filter(e => e.event_type === 'page_view');
    const pageCounts = pageViews.reduce((acc, event) => {
      const page = event.page_url;
      acc[page] = (acc[page] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(pageCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([page, count]) => ({ page, count }));
  }

  /**
   * Get top referrers
   */
  private getTopReferrers(events: AnalyticsEvent[]) {
    const referrers = events
      .filter(e => e.referrer && e.referrer !== window.location.origin)
      .map(e => e.referrer!);

    const referrerCounts = referrers.reduce((acc, referrer) => {
      acc[referrer] = (acc[referrer] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(referrerCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([referrer, count]) => ({ referrer, count }));
  }
}

// Create singleton instance
export const analyticsService = new AnalyticsService();

// React hook for analytics
export function useAnalytics() {
  return {
    trackPageView: (data?: Record<string, any>) => analyticsService.trackPageView(data),
    trackSignup: (userId: string, method: 'email' | 'phone', data?: Record<string, any>) => 
      analyticsService.trackSignup(userId, method, data),
    trackLogin: (userId: string, method: 'email' | 'phone' | 'username', data?: Record<string, any>) => 
      analyticsService.trackLogin(userId, method, data),
    trackLogout: (userId: string) => analyticsService.trackLogout(userId),
    trackInteraction: (type: string, data?: Record<string, any>) => 
      analyticsService.trackInteraction(type, data),
    getDashboardData: (timeRange?: '24h' | '7d' | '30d') => 
      analyticsService.getDashboardData(timeRange)
  };
}

export default analyticsService;
